import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  GridProps,
  Stack,
  PaperProps,
  Chip,
  Button,
  AppBar,
  Toolbar,
  AppBarProps,
  ToolbarProps,
  SwipeableDrawer,
  Typography,
  IconButton,
  Alert,
  ButtonBase,
  Tooltip,
  TextField,
  AlertProps,
  CircularProgress,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoIcon from '@mui/icons-material/Info';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import FlagIcon from '@mui/icons-material/Flag';

import { TP_COLORS } from '../../../constants';
import {
  createSubmissionAnswer,
  finishSubmission,
  getSubmissionById,
  getSubmissionQuestion,
  getSubmissionState,
  resetSubmission,
  showSubmissionQuestionAnswer,
  updateSubmissionCountdownSettings,
  startSubmissionQuestionTimer,
  endSubmissionQuestionTimer,
  ISubmissionQuestion,
  updateSubmissionQuestionOpenAiFeedback,
  createSubmissionOpenEndedAnswer,
} from '../../../store/SubmissionSlice';
import { IQuestionDetailAnswer } from '../../../store/QuestionSlice';
import { getAuthState } from '../../../store/AuthSlice';
import { getThemeMode } from '../../../store/ConfigSlice';
import { getGroupedSubTopics } from '../../../store/SubTopicSlice';
import { reportResources } from '../../../store/ResourcesSlice';
import { saveStudentProgress } from '../../../store/ProgressSlice';
import {
  createDiagnostic,
  getDiagnosticBySubmissionId,
  getDiagnosticState,
} from '../../../store/DiagnosticSlice';
import {
  getAccessLimitState,
  toggleLeadGenModal,
  togglePremiumModal,
} from '../../../store/AccessLimitSlice';
import { saveUserUsage } from '../../../store/AccessLimitSlice/AccessLimitApi';
import { useAppSelector } from '../../../hooks';
import {
  getCountry,
  getQuestionDifficultyChipColor,
  isMobileScreen,
  SafeAreaGaps,
} from '../../../helper';
import MobileHeader from '../../../components/Mobile/Header';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import DetailViewCard from '../../../components/Card/DetailViewCard';
import QuestionGridBox from './components/QuestionGridBox';
import CustomGrayBoxIconButton from '../../../components/Button/CustomGrayBoxIconButton';
import CustomGrayContainer from '../../../components/Containers/CustomGrayContainer';
import AnswerListItem from './components/AnswerListItem';
import SimpleConfirmationAlert from '../../../components/Alert/SimpleConfirmationAlert';
import CountdownTimer from './components/CountdownTimer';
import ShowAnswerModal from './modals/ShowAnswerModal';
import ContinueWithTimerModal from './modals/ContinueWithTimerModal';
import QuizTimeTakenModal from './modals/QuizTimeTakenModal';
import QuestionTimeTakenModal from './modals/QuestionTimeTakenModal';
import TopicHeader from '../../../components/Headers/TopicHeader';
import ReportModal from './modals/ReportModal';
import GradedSection from './components/GradedSection';
import LeadDetailsModal, { IDiagnosticState } from './modals/LeadDetailsModal';

const bottomNavigationHeight = '64px';
const BUTTON_BG_COLOR = 'rgba(0, 0, 0, 0.08)';

const bottomNavigationContainer: PaperProps['sx'] = {
  display: isMobileScreen() ? 'flex' : { xs: 'flex', sm: 'none' },
  position: 'fixed',
  bottom: `calc(${SafeAreaGaps.bottom} + -1px)`,
  left: SafeAreaGaps.left,
  width: '100%',
  boxShadow: 'unset',
};

const bottomAppBarStyles: AppBarProps['sx'] = {
  width: '100%',
  backgroundColor: 'transparent',
};

const bottomToolBarStyles: ToolbarProps['sx'] = {
  justifyContent: 'space-between',
  alignItems: 'center',
};

const drawerPaperStyles: PaperProps['sx'] = {
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
};

const labelStyles: React.CSSProperties = {
  borderRadius: '4px',
  fontWeight: 600,
  marginRight: '5px',
};

const questionDetailContainerStyles: PaperProps['sx'] = {
  boxShadow: 'unset',
  padding: '15px',
};

const detailGridProps: GridProps = isMobileScreen()
  ? { xs: 12 }
  : {
      xs: 12,
      sm: 12,
      md: 8,
      lg: 9,
    };

const openEndedAlertProps: AlertProps['sx'] = {
  variant: 'filled',
  severity: 'info',
  sx: {
    backgroundColor: '#707070',
    padding: '2px 12px',
    marginBottom: '15px',
    fontWeight: 600,
    fontSize: '0.8125rem',
    justifyContent: 'center',
  },
};

const ManageSubmissionQuestions: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Store
  const submissionState = useAppSelector(getSubmissionState);
  const authState = useAppSelector(getAuthState);
  const themeMode = useAppSelector(getThemeMode);
  const diagnosticState = useAppSelector(getDiagnosticState);
  const accessLimitState = useAppSelector(getAccessLimitState);

  const { diagnostic } = diagnosticState;
  const { usage, limitation } = accessLimitState;
  const isMobile = isMobileScreen();
  const authUser = authState.user;
  const userRole = authState.user?.role.name;
  const submissionData = submissionState.submission;
  const questionData = submissionState.question;
  const quizData =
    submissionData && submissionData.quiz ? submissionData.quiz : undefined;

  const quizSubTopics: string[] = [];
  const quizPapers: string[] = [];
  const topicData = quizData && quizData.topic ? quizData.topic : undefined;
  const submissionQuestions =
    submissionData && submissionData.questions ? submissionData.questions : [];

  if (quizData?.subTopics) {
    quizData.subTopics.forEach((quizSubTopic) => {
      const subTopicText = quizSubTopic.subTopic?.name || '';
      const paperText = quizSubTopic.paper || '';

      if (subTopicText && !quizSubTopics.includes(subTopicText)) {
        quizSubTopics.push(subTopicText);
      }

      if (paperText && !quizPapers.includes(paperText)) {
        quizPapers.push(paperText);
      }
    });
  }

  let quizPaperText = '';

  if (quizPapers.length) {
    quizPapers.forEach((quizPaper, index) => {
      quizPaperText += quizPaper;

      if (quizPapers.length - 1 !== index) {
        quizPaperText += ', ';
      }
    });
  }

  const isDiagnostic =
    topicData &&
    topicData.name === 'Diagnostic Challenge' &&
    topicData.curriculum === 'Cambridge IGCSE' &&
    (topicData.subject === 'Mathematics' ||
      topicData.subject === 'Physics' ||
      topicData.subject === 'Chemistry');

  const isPremiumModal =
    authUser &&
    (authUser.role.name === 'Student' || authUser.isLeadGenSubmitted);

  // State
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isShowAnswerModalOpen, setShowAnswerModal] = useState(false);
  const [isShowAnswerAlertOpen, setShowAnswerAlert] = useState(false);
  const [isTimeUpAlertVisible, setTimeUpAlertVisible] = useState(false);
  const [isQuizCompleteConfirmOpen, setQuizCompleteConfirm] = useState(false);
  const [isReportModalOpen, setReportModalOpen] = useState(false);
  const [isLeadModalOpen, setLeadModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<
    ISubmissionQuestion | undefined
  >(undefined);
  const [openEndedAnswer, setOpenEndedAnswer] = useState<string>('');

  const [
    isContinueWithTimerModalOpen,
    setContinueWithTimerModalOpen,
  ] = useState(false);

  const [
    isTimeTakenForTheQuizModalOpen,
    setTimeTakenForTheQuizModalOpen,
  ] = useState(false);

  const [
    isTimeTakenForTheQuestionModalOpen,
    setTimeTakenForTheQuestionModalOpen,
  ] = useState(false);

  const [
    isTimerSettingsModalSubmitted,
    setTimeSettingsModalSubmitted,
  ] = useState(false);

  useEffect(() => {
    dispatch(getGroupedSubTopics({ subTopicId: params.subtopicId || '' }));

    if (params.submissionId) {
      dispatch(getSubmissionById(params.submissionId));
    }

    return () => {
      dispatch(resetSubmission());

      // END QUESTION TIMER
      if (
        submissionData &&
        submissionData.status === 'pending' &&
        params.submissionId &&
        selectedQuestion &&
        selectedQuestion.question &&
        selectedQuestion.status !== 'skipped'
      ) {
        dispatch(
          endSubmissionQuestionTimer({
            submissionId: params.submissionId,
            questionId: selectedQuestion.question,
          })
        );
      }
    };
  }, [params.submissionId]);

  useEffect(() => {
    if (params.submissionId && submissionData && submissionQuestions.length) {
      if (selectedQuestion === undefined) {
        const questionItem = submissionQuestions[0];

        setSelectedQuestion(questionItem);
        if (questionItem && questionItem.question) {
          dispatch(
            getSubmissionQuestion({
              submissionId: params.submissionId,
              questionId: questionItem.question,
            })
          );
        }
      }

      if (selectedQuestion) {
        const index = submissionQuestions.findIndex(
          (questionItem) => questionItem.question === selectedQuestion.question
        );

        if (index > -1) {
          setSelectedQuestion(submissionQuestions[index]);
        }
      }

      if (
        authUser &&
        submissionData &&
        submissionData.status === 'pending' &&
        quizData?.assessment !== true &&
        authUser.isContinueSubmissionWithTimerEnabled === undefined &&
        !isTimerSettingsModalSubmitted
      ) {
        setContinueWithTimerModalOpen(true);
      }

      if (
        authState.user &&
        authState.user.isContinueSubmissionWithTimerEnabled === true &&
        submissionData &&
        submissionData.status === 'pending' &&
        submissionData.isTimerEnabled === undefined
      ) {
        dispatch(
          updateSubmissionCountdownSettings({
            rememberOption: false,
            submissionId: submissionData._id,
            isContinueSubmissionWithTimerEnabled: true,
          })
        );
      }

      if (
        isDiagnostic &&
        submissionData.status === 'completed' &&
        diagnosticState.status !== 'loading' &&
        diagnostic === undefined
      ) {
        dispatch(getDiagnosticBySubmissionId(params.submissionId));
      }
    }
  }, [params.submissionId, submissionData]);

  useEffect(() => {
    if (
      selectedQuestion &&
      params.submissionId &&
      submissionData &&
      submissionData.status === 'completed'
    ) {
      dispatch(
        getSubmissionQuestion({
          submissionId: params.submissionId,
          questionId: selectedQuestion.question,
        })
      );
    }
  }, [submissionData]);

  useEffect(() => {
    if (
      !isContinueWithTimerModalOpen &&
      params.submissionId &&
      submissionData &&
      submissionData.status === 'pending' &&
      selectedQuestion &&
      selectedQuestion.status === 'pending' &&
      !selectedQuestion.timerStart
    ) {
      dispatch(
        startSubmissionQuestionTimer({
          submissionId: params.submissionId,
          questionId: selectedQuestion.question,
        })
      );
    }
  }, [selectedQuestion, submissionData?.timerStart]);

  useEffect(() => {
    if (selectedQuestion && selectedQuestion.openEnded?.answer) {
      setOpenEndedAnswer(selectedQuestion.openEnded.answer);
    } else {
      setOpenEndedAnswer('');
    }
  }, [selectedQuestion]);

  const handleReportQuestion = (reason: string) => {
    if (params?.subtopicId && authUser?._id && questionData?._id) {
      dispatch(
        reportResources({
          questionId: questionData._id,
          subTopicId: params.subtopicId,
          userId: authUser._id,
          reason,
        })
      );
    }

    setReportModalOpen(false);
  };

  // RENDER
  if (
    (submissionData === undefined && submissionState.status === 'loading') ||
    (diagnosticState && diagnosticState.status === 'loading')
  ) {
    return (
      <div style={{ paddingTop: '30px', marginTop: '30%' }}>
        <BasicLoader />
      </div>
    );
  }

  if (submissionData) {
    // METHODS
    const handleQuestionChange = (question: ISubmissionQuestion) => {
      if (params.submissionId) {
        dispatch(
          getSubmissionQuestion({
            submissionId: params.submissionId,
            questionId: question.question,
          })
        );
      }

      // SUBMIT OPEN ENDED ANSWER
      handleAddOpenEndedAnswer();

      // END QUESTION TIMER
      if (
        submissionData &&
        submissionData.status === 'pending' &&
        params.submissionId &&
        selectedQuestion &&
        selectedQuestion.question &&
        selectedQuestion.status !== 'skipped'
      ) {
        dispatch(
          endSubmissionQuestionTimer({
            submissionId: params.submissionId,
            questionId: selectedQuestion.question,
          })
        );
      }

      setSelectedQuestion(question);

      window.scrollTo({ top: 0 });

      if (isDrawerOpen) {
        setDrawerOpen(false);
      }
    };

    const handleBackQuestionChange = () => {
      if (selectedQuestion) {
        const prevQuestion = submissionQuestions[selectedQuestion.order - 1];
        if (params.submissionId && prevQuestion) {
          dispatch(
            getSubmissionQuestion({
              submissionId: params.submissionId,
              questionId: prevQuestion.question,
            })
          );
        }

        // SUBMIT OPEN ENDED ANSWER
        handleAddOpenEndedAnswer();

        // END QUESTION TIMER
        if (
          submissionData &&
          submissionData.status === 'pending' &&
          params.submissionId &&
          selectedQuestion.question &&
          selectedQuestion.status !== 'skipped'
        ) {
          dispatch(
            endSubmissionQuestionTimer({
              submissionId: params.submissionId,
              questionId: selectedQuestion.question,
            })
          );
        }

        setSelectedQuestion(prevQuestion);
      }
    };

    const handleNextQuestionChange = () => {
      if (selectedQuestion) {
        const nextQuestion = submissionQuestions[selectedQuestion.order + 1];
        if (params.submissionId && nextQuestion) {
          dispatch(
            getSubmissionQuestion({
              submissionId: params.submissionId,
              questionId: nextQuestion.question,
            })
          );
        }

        // SUBMIT OPEN ENDED ANSWER
        handleAddOpenEndedAnswer();

        // END QUESTION TIMER
        if (
          submissionData &&
          submissionData.status === 'pending' &&
          params.submissionId &&
          selectedQuestion.question &&
          selectedQuestion.status !== 'skipped'
        ) {
          dispatch(
            endSubmissionQuestionTimer({
              submissionId: params.submissionId,
              questionId: selectedQuestion.question,
            })
          );
        }

        setSelectedQuestion(nextQuestion);
      }
    };

    const handleAddAnswer = (answer: IQuestionDetailAnswer) => {
      if (answer && answer._id && params.submissionId && questionData) {
        dispatch(
          createSubmissionAnswer({
            submissionId: params.submissionId,
            questionId: questionData._id,
            answerId: answer._id,
          })
        );

        // END QUESTION TIMER
        if (
          submissionData &&
          submissionData.status === 'pending' &&
          params.submissionId &&
          selectedQuestion &&
          selectedQuestion.question &&
          selectedQuestion.status !== 'skipped'
        ) {
          dispatch(
            endSubmissionQuestionTimer({
              submissionId: params.submissionId,
              questionId: selectedQuestion.question,
            })
          );
        }
      }
    };

    const handleAddOpenEndedAnswer = () => {
      if (
        params.submissionId &&
        selectedQuestion &&
        selectedQuestion.status !== 'skipped' &&
        submissionData.status === 'pending' &&
        openEndedAnswer
      ) {
        dispatch(
          createSubmissionOpenEndedAnswer({
            submissionId: params.submissionId,
            questionId: selectedQuestion.question,
            openEndedAnswer,
          })
        );
      }
    };

    const handleShowAnswer = () => {
      if (questionData && params.submissionId) {
        dispatch(
          showSubmissionQuestionAnswer({
            submissionId: params.submissionId,
            questionId: questionData._id,
          })
        );
      }
      setShowAnswerAlert(false);
      setShowAnswerModal(true);
    };

    const handleQuizComplete = () => {
      // SUBMIT OPEN ENDED ANSWER
      handleAddOpenEndedAnswer();

      if (params.submissionId) {
        dispatch(
          finishSubmission({
            submissionId: params.submissionId,
          })
        );
      }

      // END QUESTION TIMER
      if (
        submissionData &&
        selectedQuestion &&
        submissionData.status === 'pending' &&
        params.submissionId &&
        selectedQuestion.question &&
        selectedQuestion.status !== 'skipped'
      ) {
        dispatch(
          endSubmissionQuestionTimer({
            submissionId: params.submissionId,
            questionId: selectedQuestion.question,
          })
        );
      }

      // SAVE QUIZ PROGRESS
      if (authUser && authUser.role.name === 'Student' && params.subtopicId) {
        dispatch(
          saveStudentProgress({
            subTopicId: params.subtopicId,
            quizCompletion: true,
          })
        );
      }

      if (
        authUser &&
        (authUser.role.name === 'Guest' || authUser.role.name === 'Student')
      ) {
        dispatch(
          saveUserUsage({
            userId: authUser._id,
            option: 'quizAttempts',
          })
        );
      }

      setQuizCompleteConfirm(false);
    };

    const onShowAnswer = () => {
      if (
        authUser &&
        limitation &&
        limitation.quizAttempts &&
        limitation.quizAttempts > 0
      ) {
        dispatch(isPremiumModal ? togglePremiumModal() : toggleLeadGenModal());
      } else if (selectedQuestion?.status === 'skipped') {
        handleShowAnswer();
      } else {
        setShowAnswerAlert(true);
      }
    };

    const handleFeedback = (feedback: 'like' | 'unlike') => {
      if (
        questionData &&
        submissionData &&
        submissionData.questions &&
        submissionData.questions.length
      ) {
        const submissionQuestionData = submissionData.questions.find(
          (submissionQuestion) =>
            submissionQuestion.question === questionData._id
        );

        let openAiFeedback: -1 | 0 | 1 = 0;

        if (feedback === 'like') {
          openAiFeedback = 1;
        } else if (feedback === 'unlike') {
          openAiFeedback = -1;
        }

        if (
          submissionQuestionData?.openAiFeedback !== undefined &&
          openAiFeedback === submissionQuestionData.openAiFeedback
        ) {
          openAiFeedback = 0;
        }

        dispatch(
          updateSubmissionQuestionOpenAiFeedback({
            questionId: questionData._id,
            submissionId: submissionData._id,
            feedback: openAiFeedback,
          })
        );
      }
    };

    const handleSubmitLeadGen = (values: IDiagnosticState) => {
      if (
        values &&
        topicData &&
        params.submissionId &&
        params.subtopicId &&
        authUser
      ) {
        dispatch(
          createDiagnostic({
            ...values,
            user: authUser._id,
            submission: params.submissionId,
            curriculum: topicData.curriculum,
            subject: topicData.subject,
            subTopicId: params.subtopicId,
          })
        );
      }

      handleQuizComplete();
      setLeadModalOpen(false);
    };

    const handleSubmitButtonAction = () => {
      if (
        isDiagnostic &&
        userRole === 'Guest' &&
        !authUser?.isLeadGenSubmitted
      ) {
        setLeadModalOpen(true);
      } else if (
        !isDiagnostic &&
        limitation &&
        limitation.quizAttempts &&
        limitation.quizAttempts > 0 &&
        usage.quizAttempts + 1 >= limitation.quizAttempts
      ) {
        dispatch(isPremiumModal ? togglePremiumModal() : toggleLeadGenModal());
      } else {
        setQuizCompleteConfirm(true);
      }
    };

    const handleCreateDiagnostic = () => {
      if (topicData && params.submissionId && params.subtopicId && authUser) {
        dispatch(
          createDiagnostic({
            user: authUser._id,
            submission: params.submissionId,
            curriculum: topicData.curriculum,
            subject: topicData.subject,
            grade: topicData.grade,
            subTopicId: params.subtopicId,
            country: getCountry(),
          })
        );
      }

      handleQuizComplete();
    };

    const RENDER_SUBMISSION_TIMER = () => {
      if (isDiagnostic && submissionData.status === 'completed') {
        return null;
      }

      if (
        submissionData &&
        authUser &&
        submissionData.timerStart &&
        submissionData.isTimerEnabled
      ) {
        const _allocatedSeconds = Math.floor(submissionData.totalScore * 60);

        let _totalSubmissionSeconds = 0;
        let _isCountdownExpired = false;
        let _noCountDown = false;
        let _title = 'Time Remaining';
        let countDownVal = 0;

        if (submissionData.isTimerEnabled && submissionData.timerStart) {
          const _timestamp = new Date().getTime();
          _totalSubmissionSeconds = Math.floor(
            (_timestamp - submissionData.timerStart) / 1000
          );

          if (submissionData.status === 'pending') {
            countDownVal = _allocatedSeconds - _totalSubmissionSeconds;
            if (
              _totalSubmissionSeconds > 0 &&
              _allocatedSeconds < _totalSubmissionSeconds
            ) {
              _isCountdownExpired = true;
              _title = 'Extra Time Taken';
            }
          }

          if (submissionData.status === 'completed') {
            _title = 'Time Taken for the Quiz';
            _noCountDown = true;
            _totalSubmissionSeconds = 0;

            if (submissionData.timerEnd) {
              _totalSubmissionSeconds = Math.floor(
                (submissionData.timerEnd - submissionData.timerStart) / 1000
              );
              countDownVal = _totalSubmissionSeconds;
            }

            if (_allocatedSeconds < _totalSubmissionSeconds) {
              _isCountdownExpired = true;
              _title = 'Extra Time Taken';
              countDownVal = _allocatedSeconds - _totalSubmissionSeconds;
            }
          }
        }

        return (
          <Paper
            sx={{
              boxShadow: 'unset',
            }}
          >
            <Box
              sx={{
                p: '12px',
                mb: '10px',
                borderRadius: '7px',
                backgroundColor: 'unset',
              }}
            >
              <Grid container alignItems="center">
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    style={{ marginBottom: '5px' }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={500}
                      style={{ marginRight: '5px' }}
                    >
                      {_title}
                    </Typography>
                    {submissionData.status === 'completed' &&
                      submissionData.timerStart &&
                      submissionData.timerEnd && (
                        <IconButton
                          style={{ padding: 2, marginBottom: '3px' }}
                          onClick={() => setTimeTakenForTheQuizModalOpen(true)}
                        >
                          <InfoIcon
                            style={{
                              width: '15px',
                              height: '15px',
                            }}
                          />
                        </IconButton>
                      )}
                  </Stack>
                  <CountdownTimer
                    noCountDown={_noCountDown}
                    isExpired={_isCountdownExpired}
                    value={countDownVal}
                    behavior="decrement"
                    showSubmissionTimeUpAlert={() => {
                      if (submissionData.status === 'pending') {
                        setTimeUpAlertVisible(true);
                      }
                    }}
                    isTimeUpAlertVisible={isTimeUpAlertVisible}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        );
      }
      return null;
    };

    const RENDER_QUESTION_TIMER = () => {
      if (
        authUser &&
        selectedQuestion &&
        selectedQuestion.status !== 'skipped' &&
        selectedQuestion.timerStart &&
        selectedQuestion.timerEnd &&
        submissionData.status === 'completed' &&
        submissionData.isTimerEnabled &&
        questionData &&
        questionData._id === selectedQuestion.question
      ) {
        const _alMins = Math.round(questionData.marks * 60);
        const _val = Math.round(
          (selectedQuestion.timerEnd - selectedQuestion.timerStart) / 1000
        );

        let _exp = false;

        if (_val > _alMins) {
          _exp = true;
        }

        return (
          <Paper
            sx={{
              boxShadow: 'unset',
            }}
          >
            <Box
              sx={{
                p: '12px',
                mb: '10px',
                borderRadius: '7px',
                backgroundColor: 'unset',
              }}
            >
              <Grid container alignItems="center">
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    style={{ marginBottom: '5px' }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={500}
                      style={{ marginRight: '5px' }}
                    >
                      Time Taken for this Question
                    </Typography>
                    <IconButton
                      style={{ padding: 2, marginBottom: '3px' }}
                      onClick={() => setTimeTakenForTheQuestionModalOpen(true)}
                    >
                      <InfoIcon
                        style={{
                          width: '15px',
                          height: '15px',
                        }}
                      />
                    </IconButton>
                  </Stack>
                  <CountdownTimer
                    noCountDown
                    isExpired={_exp}
                    value={_val}
                    format="mm:ss"
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        );
      }
      return null;
    };

    const RENDER_DETAILED_REPORT_BUTTON = () => {
      if (submissionData.status === 'completed') {
        return (
          <Paper
            sx={{
              boxShadow: 'unset',
            }}
          >
            <Box
              sx={{
                p: '12px',
                mb: '10px',
                borderRadius: '7px',
                backgroundColor: 'unset',
              }}
            >
              <Button
                disabled={!diagnostic || diagnosticState.status === 'loading'}
                fullWidth
                color="primary"
                variant="contained"
                sx={{
                  fontWeight: 600,
                  maxWidth: { xs: 'unset', sm: '400px' },
                }}
                onClick={() => {
                  navigate(
                    `${process.env.PUBLIC_URL}/dashboard/my-quizzes/diagnostic/${params.subtopicId}/${diagnostic?._id}/${params.pattern}`
                  );
                }}
              >
                See Detailed Report
                {diagnosticState.status === 'loading' && (
                  <CircularProgress
                    size={25}
                    style={{ position: 'absolute' }}
                  />
                )}
              </Button>
            </Box>
          </Paper>
        );
      }
      return null;
    };

    const QUESTION_DETAILS_VIEW = (
      <div>
        <Paper style={{ boxShadow: 'unset' }}>
          <DetailViewCard
            title="Subject Level"
            titleProps={{ variant: 'caption', fontWeight: 500 }}
            description={topicData?.name || ''}
            descriptionProps={{ variant: 'subtitle2', fontWeight: 600 }}
            containerProps={{
              sx: { backgroundColor: 'unset' },
            }}
          />
        </Paper>
        <Paper style={{ boxShadow: 'unset' }}>
          <DetailViewCard
            title={`Paper${quizPapers.length > 1 ? 's' : ''}`}
            titleProps={{ variant: 'caption', fontWeight: 500 }}
            description={quizPaperText}
            descriptionProps={{ variant: 'subtitle2', fontWeight: 600 }}
            containerProps={{
              sx: { backgroundColor: 'unset' },
            }}
          />
        </Paper>

        {/* SUBMISSION TIMER */}
        {!isMobile && (
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {RENDER_SUBMISSION_TIMER()}
          </Box>
        )}

        {/* QUESTION TIMER */}
        {!isMobile && !isDiagnostic && (
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {RENDER_QUESTION_TIMER()}
          </Box>
        )}

        {/* DETAILED REPORT */}
        {!isMobile && isDiagnostic && (
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {RENDER_DETAILED_REPORT_BUTTON()}
          </Box>
        )}

        {submissionData.status === 'completed' && !isDiagnostic && (
          <Paper style={{ boxShadow: 'unset' }}>
            <DetailViewCard
              title="Score"
              titleProps={{ variant: 'caption', fontWeight: 500 }}
              description={`${submissionData.score} / ${submissionData.totalScore}`}
              descriptionProps={{ variant: 'subtitle2', fontWeight: 600 }}
              containerProps={{
                sx: { backgroundColor: 'unset' },
              }}
            />
          </Paper>
        )}
        {submissionData && questionData && submissionQuestions.length && (
          <QuestionGridBox
            selectedQuestion={selectedQuestion}
            submission={submissionData}
            question={questionData}
            onQuestionChange={handleQuestionChange}
            onQuizComplete={() => handleSubmitButtonAction()}
          />
        )}
      </div>
    );

    const SHOW_ANSWER_BUTTON =
      submissionData.status === 'pending' ? (
        <Button
          disableElevation
          size="small"
          variant="contained"
          color="inherit"
          sx={{ color: TP_COLORS.black, fontWeight: 600 }}
          onClick={onShowAnswer}
        >
          Show Answer
        </Button>
      ) : null;

    const RENDER_MANAGE_QUESTION_VIEW = () => {
      if (
        !questionData &&
        !selectedQuestion &&
        submissionState.status === 'loading'
      ) {
        return (
          <div>
            <BasicLoader />
          </div>
        );
      }

      if (selectedQuestion && questionData) {
        const QUESTION_NAVIGATION_BUTTONS = submissionData &&
          submissionQuestions.length && (
            <div>
              {selectedQuestion && selectedQuestion.order > 0 && (
                <Button
                  disableElevation
                  size="small"
                  variant="contained"
                  color="inherit"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    color: TP_COLORS.black,
                    fontWeight: 600,
                  }}
                  onClick={handleBackQuestionChange}
                >
                  Back
                </Button>
              )}
              {selectedQuestion &&
                selectedQuestion.order < submissionQuestions.length - 1 && (
                  <Button
                    disableElevation
                    size="small"
                    variant="contained"
                    color="inherit"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      color: TP_COLORS.black,
                      ml: '10px',
                      fontWeight: 600,
                    }}
                    onClick={handleNextQuestionChange}
                  >
                    Next
                  </Button>
                )}

              {/* {selectedQuestion &&
                selectedQuestion.order === submissionQuestions.length - 1 &&
                submissionData.status === 'pending' && (
                  <Button
                    disableElevation
                    size="small"
                    variant="contained"
                    color="primary"
                    endIcon={<AssignmentTurnedInIcon />}
                    sx={{
                      ml: '10px',
                      fontWeight: 600,
                    }}
                    onClick={() => setQuizCompleteConfirm(true)}
                  >
                    Complete
                  </Button>
                )} */}
            </div>
          );
        const permutator = (answers) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result = [] as any;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const permute = (options, item: any = []) => {
            if (options.length === 0) {
              result.push(item);
            } else {
              for (let i = 0; i < options.length; i += 1) {
                const current = options.slice();
                const next = current.splice(i, 1);
                permute(current.slice(), item.concat(next));
              }
            }
          };
          permute(answers);
          return result;
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const permutationsNumber = [] as any;
        const factorial = (n) => {
          if (n === 0 || n === 1) return 1;
          if (permutationsNumber[n] > 0) return permutationsNumber[n];
          permutationsNumber[n] = factorial(n - 1) * n;
          return permutationsNumber[n];
        };

        let _questionShuffled;
        let selectedShuffledQuestionAnswer;
        let shuffledCorrectAnswers;

        if (submissionData && submissionData.shuffleFactor) {
          const selectedShuffle = permutator(questionData.answers)[
            submissionData.shuffleFactor %
              factorial(questionData.answers.length)
          ];

          _questionShuffled = selectedShuffle.map((item, index) => ({
            ...item,
            order: index,
          }));
        } else {
          _questionShuffled = questionData.answers;
        }

        if (_questionShuffled && _questionShuffled.length) {
          shuffledCorrectAnswers = _questionShuffled.filter(
            (shuffledQuestion) =>
              shuffledQuestion.isCorrect && shuffledQuestion.openaiResponse
          );

          selectedShuffledQuestionAnswer = _questionShuffled.find(
            (shuffledQuestion) =>
              shuffledQuestion._id === selectedQuestion.answer?._id
          );
        }

        return (
          <div>
            {isDiagnostic ? (
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {RENDER_DETAILED_REPORT_BUTTON()}
              </Box>
            ) : (
              <>
                {/* SUBMISSION TIMER MOBILE */}
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                  {RENDER_SUBMISSION_TIMER()}
                </Box>

                {/* QUESTION TIMER */}
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                  {RENDER_QUESTION_TIMER()}
                </Box>
              </>
            )}

            {isTimeUpAlertVisible && submissionData.status === 'pending' && (
              <div>
                <Alert
                  variant="filled"
                  severity="warning"
                  color="warning"
                  icon={false}
                  sx={{
                    margin: '15px 0',
                    color: 'text.primary',
                    fontWeight: 600,
                    justifyContent: 'center',
                  }}
                >
                  Time up! You may continue to practice but your score will not
                  change
                </Alert>
              </div>
            )}

            <Paper sx={{ ...questionDetailContainerStyles, mb: '15px' }}>
              {/* TOP LABELS AND BUTTONS */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box display="flex" alignItems="center">
                  <Chip
                    label={`Question ${(selectedQuestion.order + 1)
                      .toString()
                      .padStart(2, '0')}`}
                    style={labelStyles}
                  />
                  <Chip
                    label={`${questionData.marks
                      .toString()
                      .padStart(2, '0')} Marks`}
                    style={labelStyles}
                  />
                  <Chip
                    label={questionData.difficulty}
                    style={labelStyles}
                    color={getQuestionDifficultyChipColor(
                      questionData.difficulty
                    )}
                  />
                  <Tooltip title="Report this question">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setReportModalOpen(true);
                      }}
                      style={{
                        backgroundColor: BUTTON_BG_COLOR,
                        borderRadius: '4px',
                      }}
                    >
                      <FlagIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                {!isMobile && (
                  <Box
                    alignItems="center"
                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                  >
                    {QUESTION_NAVIGATION_BUTTONS}
                  </Box>
                )}
              </Stack>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ mt: '15px' }}
              >
                {selectedQuestion.status === 'skipped' && (
                  <Chip label="Skipped" style={labelStyles} />
                )}
                {SHOW_ANSWER_BUTTON}
              </Stack>
              {/* QUESTION DETAILS */}
              <div style={{ padding: '15px 0' }}>
                {selectedQuestion.isOpenEnded &&
                questionData.openEnded &&
                questionData.openEnded.question ? (
                  <>
                    {submissionData.status === 'pending' && (
                      <Alert {...openEndedAlertProps} icon={false}>
                        Your tutor will assess theory-type questions upon
                        submission, and final scores will be made available at a
                        later time.
                      </Alert>
                    )}
                    <div
                      className="view ql-editor ql-viewer"
                      style={{ marginBottom: '20px' }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: questionData.openEnded.question.startsWith('.')
                          ? (questionData.openEnded.question || '')
                              .trim()
                              .substring(1)
                          : (questionData.openEnded.question || '').trim(),
                      }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={6}
                      disabled={
                        selectedQuestion?.status === 'skipped' ||
                        submissionData.status === 'completed'
                      }
                      placeholder={
                        submissionData.status === 'pending' &&
                        selectedQuestion?.status === 'pending'
                          ? 'Type your answer here'
                          : ''
                      }
                      value={openEndedAnswer}
                      onChange={(event) =>
                        setOpenEndedAnswer(event.currentTarget.value)
                      }
                    />
                    {submissionData?.status === 'completed' && (
                      <GradedSection {...{ selectedQuestion, questionData }} />
                    )}
                  </>
                ) : (
                  <>
                    <div
                      className="view ql-editor ql-viewer"
                      style={{ marginBottom: '20px' }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: questionData.details || '',
                      }}
                    />
                    {/* ANSWER LIST */}
                    <div>
                      <Grid container spacing={2}>
                        {_questionShuffled.map((answer, index) => (
                          <Grid key={index} item xs={12}>
                            <AnswerListItem
                              submission={submissionData}
                              answer={answer}
                              answers={_questionShuffled}
                              selectedQuestion={selectedQuestion}
                              onClick={
                                submissionData.status === 'pending' &&
                                selectedQuestion.status !== 'skipped'
                                  ? () => handleAddAnswer(answer)
                                  : undefined
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </div>
                  </>
                )}
              </div>
            </Paper>
            {/* MARKING SCHEME */}
            {submissionData.status === 'completed' &&
            shuffledCorrectAnswers &&
            shuffledCorrectAnswers.length ? (
              <Paper
                sx={{ ...questionDetailContainerStyles, marginBottom: '15px' }}
              >
                <Stack direction="row" alignItems="center" sx={{ mb: '15px' }}>
                  <Chip label="Detailed Marking Scheme" style={labelStyles} />
                  <Chip
                    size="small"
                    label="AI Powered"
                    style={{
                      ...labelStyles,
                      backgroundColor:
                        themeMode === 'dark'
                          ? TP_COLORS.white
                          : TP_COLORS.black,
                      color:
                        themeMode === 'dark'
                          ? TP_COLORS.black
                          : TP_COLORS.white,
                    }}
                  />
                </Stack>
                {selectedShuffledQuestionAnswer &&
                selectedShuffledQuestionAnswer.isCorrect === false ? (
                  <>
                    <div style={{ marginBottom: '15px' }}>
                      <Chip
                        size="small"
                        color="warning"
                        label="Why your answer is wrong"
                        style={{ ...labelStyles, color: TP_COLORS.white }}
                      />
                    </div>
                    <Stack>
                      <div style={{ marginBottom: '15px' }}>
                        {!(
                          selectedQuestion.isOpenEnded && questionData.openEnded
                        ) ? (
                          <div>
                            <Typography>
                              <span style={{ fontWeight: 600 }}>
                                Answer:{' '}
                                {(selectedShuffledQuestionAnswer.order + 1)
                                  .toString()
                                  .padStart(2, '0')}{' '}
                                )
                              </span>
                            </Typography>
                          </div>
                        ) : null}
                        <div>
                          <div
                            className="view ql-editor ql-viewer"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: (
                                selectedShuffledQuestionAnswer.content || ''
                              ).trim(),
                            }}
                          />
                        </div>
                        {selectedShuffledQuestionAnswer && (
                          <div>
                            <div
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: (
                                  selectedShuffledQuestionAnswer.openaiResponse ||
                                  ''
                                ).trim(),
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </Stack>
                  </>
                ) : null}
                {shuffledCorrectAnswers && shuffledCorrectAnswers.length ? (
                  <>
                    <div style={{ marginBottom: '15px' }}>
                      <Chip
                        size="small"
                        color="success"
                        label={`Correct Answer${
                          shuffledCorrectAnswers.length > 1 ? 's' : ''
                        }`}
                        style={{ ...labelStyles, color: TP_COLORS.white }}
                      />
                    </div>
                    {shuffledCorrectAnswers.map(
                      (shuffledCorrectAnswer, index) => (
                        <Stack key={index}>
                          <div style={{ marginBottom: '15px' }}>
                            {!(
                              selectedQuestion.isOpenEnded &&
                              questionData.openEnded
                            ) ? (
                              <div>
                                <Typography>
                                  <span style={{ fontWeight: 600 }}>
                                    Answer:{' '}
                                    {(shuffledCorrectAnswer.order + 1)
                                      .toString()
                                      .padStart(2, '0')}{' '}
                                    )
                                  </span>
                                </Typography>
                              </div>
                            ) : null}
                            <div>
                              <div
                                className="view ql-editor ql-viewer"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                  __html: (
                                    shuffledCorrectAnswer.content || ''
                                  ).trim(),
                                }}
                              />
                            </div>
                            {selectedShuffledQuestionAnswer &&
                              selectedShuffledQuestionAnswer.isCorrect ===
                                true && (
                                <div>
                                  <div
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                      __html: (
                                        selectedShuffledQuestionAnswer.openaiResponse ||
                                        ''
                                      ).trim(),
                                    }}
                                  />
                                </div>
                              )}
                            {!selectedShuffledQuestionAnswer &&
                              shuffledCorrectAnswers &&
                              shuffledCorrectAnswers.length &&
                              shuffledCorrectAnswers[0].openaiResponse && (
                                <div>
                                  <div
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                      __html: (
                                        shuffledCorrectAnswers[0]
                                          .openaiResponse || ''
                                      ).trim(),
                                    }}
                                  />
                                </div>
                              )}
                          </div>
                        </Stack>
                      )
                    )}
                  </>
                ) : null}
                <Stack direction="row">
                  <ButtonBase
                    color="info"
                    onClick={() => handleFeedback('like')}
                    disabled={submissionState.status === 'loading'}
                    sx={{
                      p: '5px',
                      borderRadius: '4px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'info.main',
                      marginRight: '10px',
                      backgroundColor:
                        themeMode === 'light' ? 'info.light' : undefined,
                    }}
                  >
                    {selectedQuestion.openAiFeedback === 1 ? (
                      <ThumbUpIcon color="info" />
                    ) : (
                      <ThumbUpOffAltIcon color="info" />
                    )}
                  </ButtonBase>
                  <ButtonBase
                    color="info"
                    disabled={submissionState.status === 'loading'}
                    onClick={() => handleFeedback('unlike')}
                    sx={{
                      p: '5px',
                      borderRadius: '4px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'info.main',
                      backgroundColor:
                        themeMode === 'light' ? 'info.light' : undefined,
                    }}
                  >
                    {selectedQuestion.openAiFeedback === -1 ? (
                      <ThumbDownIcon color="info" />
                    ) : (
                      <ThumbDownOffAltIcon color="info" />
                    )}
                  </ButtonBase>
                </Stack>
              </Paper>
            ) : null}
            {submissionData.status === 'completed' && (
              <Paper sx={questionDetailContainerStyles}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: '15px' }}
                >
                  <Chip label="Marking Scheme" style={labelStyles} />
                </Stack>
                <div>
                  <div
                    className="view ql-editor ql-viewer"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: questionData.markingSchema || '',
                    }}
                  />
                </div>
              </Paper>
            )}
            {/* BOTTOM GAP FOR BOTTOM BAR */}
            <Box
              sx={{
                minHeight: isMobile
                  ? bottomNavigationHeight
                  : { xs: bottomNavigationHeight, sm: '0' },
              }}
            />

            {/* BOTTOM NAVIGATION */}
            <Paper sx={bottomNavigationContainer}>
              <AppBar position="relative" sx={bottomAppBarStyles}>
                <Toolbar sx={bottomToolBarStyles}>
                  <div>
                    <CustomGrayBoxIconButton
                      onClick={() => setDrawerOpen(true)}
                    >
                      <GridViewIcon />
                    </CustomGrayBoxIconButton>
                  </div>
                  {SHOW_ANSWER_BUTTON}
                  <div>{QUESTION_NAVIGATION_BUTTONS}</div>
                </Toolbar>
              </AppBar>
            </Paper>
            {/* DRAWER */}
            <SwipeableDrawer
              anchor="bottom"
              open={isDrawerOpen}
              onOpen={() => {
                if (!isDrawerOpen) {
                  setDrawerOpen(true);
                }
              }}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{ sx: drawerPaperStyles }}
            >
              <CustomGrayContainer
                component="div"
                role="presentation"
                sx={{ pb: SafeAreaGaps.bottom }}
              >
                {QUESTION_DETAILS_VIEW}
              </CustomGrayContainer>
            </SwipeableDrawer>
            {/* ALERTS */}
            <SimpleConfirmationAlert
              open={isShowAnswerAlertOpen}
              onClose={() => setShowAnswerAlert(false)}
              title="Show Answer"
              description="By clicking Continue, you will no longer be able to answer the question."
              cancelButtonProps={{
                label: 'Cancel',
                onClick: () => setShowAnswerAlert(false),
              }}
              okButtonprops={{
                label: 'Continue',
                onClick: handleShowAnswer,
              }}
            />
            <SimpleConfirmationAlert
              open={isQuizCompleteConfirmOpen}
              onClose={() => setQuizCompleteConfirm(false)}
              title="Are you sure?"
              description="Do you want to complete this quiz?"
              cancelButtonProps={{
                label: 'Cancel',
                onClick: () => setQuizCompleteConfirm(false),
              }}
              okButtonprops={{
                label: 'Complete',
                onClick: () =>
                  isDiagnostic
                    ? handleCreateDiagnostic()
                    : handleQuizComplete(),
              }}
            />
            {/* MODALS */}
            <ShowAnswerModal
              open={isShowAnswerModalOpen}
              question={questionData}
              onClose={() => setShowAnswerModal(false)}
            />
            <ContinueWithTimerModal
              open={isContinueWithTimerModalOpen}
              onClose={() => setContinueWithTimerModalOpen(false)}
              onConfirm={() => setTimeSettingsModalSubmitted(true)}
            />
            {isTimeTakenForTheQuizModalOpen && (
              <QuizTimeTakenModal
                open={isTimeTakenForTheQuizModalOpen}
                onClose={() => setTimeTakenForTheQuizModalOpen(false)}
              />
            )}
            {isTimeTakenForTheQuestionModalOpen && selectedQuestion && (
              <QuestionTimeTakenModal
                open={isTimeTakenForTheQuestionModalOpen}
                onClose={() => setTimeTakenForTheQuestionModalOpen(false)}
                question={selectedQuestion}
              />
            )}
            {isReportModalOpen && (
              <ReportModal
                open={isReportModalOpen}
                type="question"
                onClose={() => setReportModalOpen(false)}
                onConfirm={handleReportQuestion}
              />
            )}
            {isLeadModalOpen && (
              <LeadDetailsModal
                open={isLeadModalOpen}
                onClose={() => setLeadModalOpen(false)}
                onConfirm={handleSubmitLeadGen}
              />
            )}
          </div>
        );
      }
      return null;
    };

    return (
      <div>
        {params.subtopicId ? (
          <MobileHeader
            leftContents={{
              type: 'subtopic-nav-bar',
            }}
          />
        ) : (
          <MobileHeader
            leftContents={{
              type: 'backButton',
              iconButtonProps: {
                onClick: () => navigate(-1),
              },
            }}
          />
        )}
        <TopicHeader
          subject={topicData?.subject || ''}
          topic={topicData?.name || ''}
          subTopics={quizSubTopics}
        />
        <Box component="div" sx={{ padding: { xs: '9px', sm: '15px' } }}>
          <Grid container spacing={2}>
            {!isMobile && (
              <Grid
                item
                xs={12}
                sm={12}
                md={4}
                lg={3}
                sx={{
                  display: isMobile ? 'none' : { xs: 'none', sm: 'block' },
                }}
              >
                <Box
                  sx={{
                    position: { xs: 'static', sm: 'static', md: 'sticky' },
                    top: {
                      xs: 'unset',
                      sm: 'unset',
                      md: params.subtopicId ? 'calc(15px + 64px)' : '15px',
                    },
                  }}
                >
                  {QUESTION_DETAILS_VIEW}
                </Box>
              </Grid>
            )}
            <Grid item {...detailGridProps}>
              {RENDER_MANAGE_QUESTION_VIEW()}
            </Grid>
          </Grid>
        </Box>
      </div>
    );
  }

  return null;
};

export default ManageSubmissionQuestions;
